import jsonLogic from "json-logic-js";
import { each, every, extend, filter, find, flatten, get, has, includes, intersectionBy, isEmpty, map, max, merge, min, partition, sample, size, split, template, templateSettings, uniqBy, xorBy, zip, } from 'lodash';

import { processGroups, processOptions } from './import';

// Check the existing imported items are valid
const hasValidImport = (importLogic, allowedItems, persistedItems) => {
  return every(map(importLogic, (item) => {
    const allowedSubset = intersectionBy(allowedItems, item.input, 'name');
    const persistedSubset = intersectionBy(persistedItems, item.input, 'name');

    // If limit is defined then make sure the existing state is included and has at least the same size of the limit defined
    if (has(item, 'limit')) {
      const commonItems = intersectionBy(allowedSubset, persistedSubset, 'name');
      return size(commonItems) === min([ get(item, 'limit'), max([ size(allowedSubset), size(persistedSubset) ]) ]);
    } else {
      const exclusive = xorBy(allowedSubset, persistedSubset, 'name');
      return size(exclusive) === 0;
    }
  }));
};

// Check the existing native items are valid
const hasValidNative = (allowItems, persistedItems) => {
  const exclusive = xorBy(allowItems, persistedItems, 'name');
  return size(exclusive) === 0;
};

// Check the existing items are valid according to the current state
const hasValidItems = (allowedItems, persistedItems, importLogic, uniqueLogic) => {
  // Create a list of imported items with their sources
  const importedList = flatten(map(importLogic, item => map(item.input, entry => ({ name: entry.name, source: entry.source }))));

  // Append source field to both the allowed items and the existing items
  const referenceList = map(allowedItems, item => {
    const ref = find(importedList, { name: item.name });
    return { ...item, source: get(ref, 'source')};
  });

  const persistedList = map(persistedItems, item => {
    const ref = find(importedList, { name: item.name });
    return { ...item, source: get(ref, 'source')};
  });

  // Split imported and native items
  const [importedAllowedItems, nativeAllowedItems] = partition(referenceList, 'source');
  const [importedPersistedItems, nativePersistedItems] = partition(persistedList, 'source');

  // First remove non-unique items if such item exists in the persisted response
  const uniqueList = zip(...uniqueLogic);

  const output = uniqBy(flatten(map(importedAllowedItems, item => {
    const source = get(item, 'source', null);

    // Find equivalent entries to this source
    const equivalentList = flatten(filter(uniqueList, uniqueItem => includes(uniqueItem, source)));

    // Find items with this source in the persisted entries
    const preferredList = filter(importedPersistedItems, persistedItem => includes([...equivalentList, source], get(persistedItem, 'source', null)));

    return isEmpty(preferredList) ? [item] : preferredList;
  })), 'name');

  // Items are valid if both imported and native items are
  const isValidImport = hasValidImport(importLogic, output, importedPersistedItems);
  const isValidNative = hasValidNative(nativeAllowedItems, nativePersistedItems);

  return isValidImport && isValidNative;
};

// A context is valid if both the groups and options are consistent according to the current state
// Note that for questions not having neither of these properties (such as TEXTAREA) the result will be true as well
export function isContextValid(question, displayContext, context) {
  const importLogic = get(question, 'importLogic', []);
  const uniqueLogic = get(question, 'uniqueLogic', []);

  // TODO: we should be able to use the same context structure used in "displayContext" to save passing the extra "context" argument
  const ctx = context[question.name];

  const allowedGroups = processGroups(question, displayContext, { limit: false, unique: false });
  const persistedGroups = get(ctx, 'groups', []);

  const allowedOptions = processOptions(question, displayContext, { limit: false, unique: false });
  const persistedOptions = get(ctx, 'options', []);

  // Filter out import logic applied to groups
  const groupNames = map(get(question, 'groups', []), group => group.name);
  const importLogicGroups = filter(importLogic, item => every(get(item, 'input', []), i => includes(groupNames, i.name)));
  const validGroups = hasValidItems(allowedGroups, persistedGroups, importLogicGroups, uniqueLogic);

  // Filter out import logic applied to options
  const optionNames = map(get(question, 'options', []), option => option.name);
  const importLogicOptions = filter(importLogic, item => every(get(item, 'input', []), i => includes(optionNames, i.name)));
  const validOptions = hasValidItems(allowedOptions, persistedOptions, importLogicOptions, uniqueLogic);

  return validGroups && validOptions;
}

export function generateContextVariables(question, value) {
  let custom = {};

  each(question.contextVariable, ctx => {
    // Recreate context object as expected by the logic definition
    const context = { answer: { [question.name]: { answer: value } } };

    // Filter out and obtain the items which evaluate to true according to the logic definition
    const filtered = filter(ctx.input, input => jsonLogic.apply(input.logic, context));
    const mapped = map(filtered, item => item.source);

    // TODO: hardcode limit to 1 but this may need to be revised
    const output = sample(mapped);

    // Generate context object for this question
    const obj = { [ctx.name]: output };
    // console.log(obj);

    merge(custom, obj);
  });

  // console.log(custom);

  return custom;
}

/**
 * Returns an interpolated text based on current context
 * @param {*} field
 * @param {*} context
 * @param {*} questions: containing the current localised text content
 */
export function resolveContextVariable(field, context, questions) {
  try {
    // Convert context into a dictionary where the question is the root property and the variables its children
    // This way the field can be formatted in the form ${ questionId.variableId } which is easier to understand
    // and does not expose low level details to the user
    let ctx = {};
    each(context, (v, k) => ctx[k] = get(v, 'custom'));

    // Helper method to obtain the text property of a specific item
    const text = (id) => {
      const [scope, qid, oid] = split(id, '.');

      const question = find(questions, { name: qid });
      const items = scope === 'option' ? 'options' : scope === 'group' ? 'groups' : null;
      const item = find(question[items], { name: oid });

      return item.text;
    };

    ctx = extend({ text }, ctx);

    templateSettings.interpolate = /{{([\s\S]+?)}}/g;
    const compiled = template(field);
    return compiled(ctx);
  } catch(error) {
    console.error(error);
    return field;
  }
}

// Generate group and option items to store in context document by creating a lean object from the original
export function generateGroupItem(group) {
  return { name: group.name };
}

export function generateOptionItem(option) {
  return { name: option.name, value: option.value };
}
